/** Shared "problem" (Blocked/Unresolved/Not SSO/...) + role (Owner/Admin/Member) filtering for a
 * per-service tab (Licenses and User links both use this — same filters everywhere, no
 * per-page-invented variants). Reads/writes `?problem=` and `?role=` in the URL so the
 * Dashboard's alert buttons can deep-link straight to the matching rows on either tab. */
import { useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { buildProblemFilters, findDuplicateLicenseKeys } from '../lib/personRows'
import type { PersonRow, ProblemFilter } from '../lib/personRows'

export function useProblemFilters(rows: PersonRow[], staleLoginDays: number) {
  const [searchParams, setSearchParams] = useSearchParams()
  // Duplicate detection needs the full assignment set across rows (an alias + primary email for
  // the same person are two rows with one shared license key), so it's computed here once and
  // passed into the shared filter definitions.
  const duplicateKeys = useMemo(
    () => findDuplicateLicenseKeys(rows.flatMap(row => Object.values(row.byService).flat())),
    [rows],
  )
  const problemFilters = useMemo(
    () => buildProblemFilters(staleLoginDays, duplicateKeys),
    [staleLoginDays, duplicateKeys],
  )

  const requestedFilter = searchParams.get('problem')
  const problemFilter: ProblemFilter = problemFilters.some(f => f.value === requestedFilter)
    ? (requestedFilter as ProblemFilter)
    : 'all'

  // Picking a problem filter clears any active role filter and vice versa (below) — they used to
  // be independent, combinable dimensions, but stacking them silently (e.g. "Admin" clicked while
  // "Stale login" was still selected from an earlier click) produced a confusing empty "No one
  // matches" result with no obvious cause. Only one preset is active at a time now, like a normal
  // filter chip row.
  function setProblemFilter(value: ProblemFilter) {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev)
      if (value === 'all') next.delete('problem')
      else next.set('problem', value)
      next.delete('role')
      return next
    })
  }

  const roleFilter = searchParams.get('role')
  function setRoleFilter(value: string | null) {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev)
      if (!value || value === roleFilter) next.delete('role')
      else next.set('role', value)
      next.delete('problem')
      return next
    })
  }

  const activeFilter = problemFilters.find(f => f.value === problemFilter) ?? problemFilters[0]
  const hasRoles = rows.some(row => row.role)

  return { problemFilters, problemFilter, setProblemFilter, roleFilter, setRoleFilter, activeFilter, hasRoles, duplicateKeys }
}
